import React, { useLayoutEffect, useRef, useState } from 'react';
import { EdgeLabelRenderer, getBezierPath } from 'reactflow';

const THEME = {
    primary: '#2cbb5d',
    secondary: '#a1a1aa',
    accent: '#7ee8a8',
    active: '#facc15',
    selected: '#38bdf8',
};

function arrowPolygon(x0, y0, tipX, tipY, size = 11) {
    const dx = tipX - x0;
    const dy = tipY - y0;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const bx = tipX - ux * size;
    const by = tipY - uy * size;
    const px = -uy * (size * 0.55);
    const py = ux * (size * 0.55);
    return `${tipX},${tipY} ${bx + px},${by + py} ${bx - px},${by - py}`;
}

/** Sample two points on the rendered path so the arrow sits on the curve before the target. */
function usePathArrow(pathD, tipAt = 0.86, backAt = 0.74) {
    const pathRef = useRef(null);
    const [arrow, setArrow] = useState(null);

    useLayoutEffect(() => {
        const el = pathRef.current;
        if (!el || !pathD) {
            setArrow(null);
            return;
        }
        const len = el.getTotalLength();
        if (!len) {
            setArrow(null);
            return;
        }
        const tip = el.getPointAtLength(len * tipAt);
        const back = el.getPointAtLength(len * backAt);
        setArrow({ tipX: tip.x, tipY: tip.y, backX: back.x, backY: back.y });
    }, [pathD, tipAt, backAt]);

    return { pathRef, arrow };
}

export default function MarkovGlowEdge({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    label,
    data,
    selected,
}) {
    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        curvature: 0.35,
    });

    const { pathRef, arrow } = usePathArrow(edgePath, 0.86, 0.74);

    const state = data?.solutionState || 'off';
    const isActive = state === 'active';
    const isDone = state === 'done';
    const isUpcoming = state === 'upcoming';
    const isPolicy = state === 'policy';
    const isFlow = state === 'flow';
    const isDim = state === 'dim';

    let stroke = THEME.secondary;
    let strokeWidth = 2.25;
    let opacity = 0.95;
    let className = 'markov-edge';

    if (selected) {
        stroke = THEME.selected;
        strokeWidth = 3;
        className += ' markov-edge--selected';
    } else if (isActive) {
        stroke = THEME.active;
        strokeWidth = 3.5;
        className += ' markov-edge--active';
    } else if (isDone) {
        stroke = THEME.primary;
        strokeWidth = 3;
        className += ' markov-edge--done';
    } else if (isUpcoming) {
        stroke = THEME.primary;
        strokeWidth = 2;
        opacity = 0.35;
        className += ' markov-edge--upcoming';
    } else if (isPolicy) {
        stroke = THEME.accent;
        strokeWidth = 2.75;
        className += ' markov-edge--policy';
    } else if (isFlow) {
        stroke = THEME.primary;
        strokeWidth = 2.25;
        opacity = 0.55 + (data?.flowStrength || 0) * 0.45;
        className += ' markov-edge--flow';
    } else if (isDim) {
        opacity = 0.18;
    }

    const highlight = isActive || isDone || isPolicy || selected;

    return (
        <g className={className} opacity={opacity}>
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={24}
                className="react-flow__edge-interaction"
            />
            {(isActive || isDone) && (
                <path
                    d={edgePath}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={isActive ? 12 : 8}
                    opacity={isActive ? 0.35 : 0.2}
                    className="markov-edge-glow"
                />
            )}
            <path
                ref={pathRef}
                id={id}
                d={edgePath}
                fill="none"
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                className="markov-edge-path"
            />
            {arrow && (
                <polygon
                    points={arrowPolygon(arrow.backX, arrow.backY, arrow.tipX, arrow.tipY, 11)}
                    fill={stroke}
                    className="markov-arrow-head"
                />
            )}
            {label != null && label !== '' && (
                <EdgeLabelRenderer>
                    <div
                        className={`nodrag nopan markov-edge-label${highlight ? ' markov-edge-label--on' : ''}`}
                        title="Double-click to edit weight"
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                            pointerEvents: 'all',
                            cursor: 'pointer',
                        }}
                        onDoubleClick={(ev) => {
                            ev.stopPropagation();
                            data?.onEditWeight?.();
                        }}
                    >
                        {label}
                    </div>
                </EdgeLabelRenderer>
            )}
        </g>
    );
}
